import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  XCircle,
  Archive,
  AlertCircle,
  MessageSquare,
  Send,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { CenterSpinner } from '@/components/ui/Spinner'
import { Tabs, type TabSpec } from '@/components/ui/Tabs'
import { Modal } from '@/components/ui/Modal'
import { Textarea, Label, Input } from '@/components/ui/Input'
import { PdfPreview } from '@/components/ui/PdfPreview'
import { TagEditor } from '@/components/invoices/TagEditor'
import { AiSuggestionsPanel } from '@/components/invoices/AiSuggestionsPanel'
import {
  CountryBadge,
  MatchingScoreBadge,
  MatchingStatusBadge,
  PaymentStatusBadge,
  SourceBadge,
  WorkflowStatusBadge,
} from '@/components/ui/StatusBadge'
import {
  useAddInvoiceComment,
  useApproveInvoice,
  useArchiveInvoice,
  useInvoice,
  useInvoiceComments,
  useInvoicePdf,
  useRecordPayment,
  useUpdateCostAllocation,
  useUpdateInvoiceStatus,
  useUsers,
} from '@/lib/api/hooks'
import { useToast } from '@/components/ui/ToastProvider'
import { useAuth } from '@/lib/auth/AuthProvider'
import { hasPermission } from '@/lib/permissions'
import { dateShort, dateTime, money, moneyCompact, percent } from '@/lib/format'
import { hu } from '@/lib/i18n/hu'
import type { CostAllocationPayload, InvoiceDetail, InvoiceSplit } from '@/lib/api/types'

export function InvoiceDetailPage() {
  const { id } = useParams()
  const nav = useNavigate()
  const { activeRole } = useAuth()
  const { data, isLoading } = useInvoice(id)
  const toast = useToast()

  const approve = useApproveInvoice()
  const archive = useArchiveInvoice()
  const updateStatus = useUpdateInvoiceStatus()

  const [archiveReason, setArchiveReason] = useState('')
  const [archiveOpen, setArchiveOpen] = useState(false)

  const pdfQuery = useInvoicePdf(id, !!data?.files.pdf)

  if (isLoading || !data) return <CenterSpinner />

  const canApprove = hasPermission(activeRole, 'invoices.approve')
  const canArchive = hasPermission(activeRole, 'invoices.archive')
  const canChangeStatus = hasPermission(activeRole, 'invoices.changeStatus')

  const tabs: TabSpec[] = [
    { key: 'matching', label: 'Matching', content: <MatchingPanel inv={data} /> },
    { key: 'source', label: 'Forrás', content: <SourcePanel inv={data} /> },
    { key: 'audit', label: 'Audit', content: <AuditPanel inv={data} /> },
    { key: 'payment', label: 'Fizetés', content: <PaymentPanel inv={data} /> },
    { key: 'cost', label: 'Költségfelosztás', content: <CostAllocationPanel inv={data} /> },
    { key: 'comments', label: 'Megjegyzések', content: <CommentsPanel invoiceId={data.id} /> },
  ]

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <Link
          to="/invoices"
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> {hu.common.backToList}
        </Link>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-1">
          <Card padding="none" className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2">
              <span className="text-xs uppercase tracking-wide text-slate-500">PDF</span>
              {data.files.pdf && (
                <button
                  type="button"
                  onClick={() => downloadPdf(pdfQuery.data, data.invoiceNumber ?? data.id)}
                  disabled={!pdfQuery.data}
                  className="text-xs text-brand-600 hover:underline inline-flex items-center gap-1 disabled:text-slate-400"
                >
                  <Download className="h-3 w-3" /> letöltés
                </button>
              )}
            </div>
            {data.files.pdf ? (
              <PdfPreview bytes={pdfQuery.data ?? null} className="h-[640px] xl:h-[760px]" />
            ) : (
              <div className="aspect-[3/4] grid place-items-center bg-slate-100 text-slate-400 text-sm">
                <div className="text-center">
                  <AlertCircle className="mx-auto h-10 w-10 mb-2 opacity-60" />
                  Nincs PDF
                </div>
              </div>
            )}
          </Card>
        </div>

        <div className="xl:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <div>
                <CardTitle>
                  <span className="font-mono">{data.invoiceNumber ?? '—'}</span> · {data.supplier.name ?? '—'}
                </CardTitle>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <SourceBadge source={data.source} />
                  <CountryBadge type={data.countryType} />
                  <MatchingStatusBadge status={data.matching.status} />
                  <MatchingScoreBadge score={data.matching.score} />
                  <WorkflowStatusBadge status={data.workflowStatus} />
                  <PaymentStatusBadge status={data.paymentStatus} />
                </div>
                <div className="mt-2">
                  <TagEditor
                    invoiceId={data.id}
                    tags={data.tags ?? []}
                    canEdit={canChangeStatus}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {canApprove && data.approval.status === 'pending' && (
                  <Button
                    onClick={async () => {
                      await approve.mutateAsync({ id: data.id })
                      toast.show({ tone: 'success', title: 'Jóváhagyva' })
                    }}
                    leftIcon={<CheckCircle2 className="h-4 w-4" />}
                    loading={approve.isPending}
                  >
                    Jóváhagy
                  </Button>
                )}
                {canChangeStatus && data.workflowStatus !== 'disputed' && (
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      await updateStatus.mutateAsync({
                        id: data.id,
                        toStatus: 'disputed',
                        reason: 'Vitásnak jelölve a részletező oldalról',
                      })
                      toast.show({ tone: 'warning', title: 'Vitásnak jelölve' })
                    }}
                    leftIcon={<XCircle className="h-4 w-4" />}
                  >
                    Vitásnak jelöl
                  </Button>
                )}
                {canArchive && data.workflowStatus !== 'archived' && (
                  <Button
                    variant="ghost"
                    onClick={() => setArchiveOpen(true)}
                    leftIcon={<Archive className="h-4 w-4" />}
                  >
                    Archivál
                  </Button>
                )}
              </div>
            </CardHeader>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Field label="Szállító">
                <div>{data.supplier.name ?? '—'}</div>
                <div className="text-xs text-slate-500">{data.supplier.taxNumber ?? '—'} · {data.supplier.countryCode ?? '—'}</div>
              </Field>
              <Field label="Vevő">
                <div>{data.buyer.name ?? '—'}</div>
                <div className="text-xs text-slate-500">{data.buyer.taxNumber ?? '—'} · {data.buyer.countryCode ?? '—'}</div>
              </Field>
              <Field label="Kelt">{dateShort(data.issueDate)}</Field>
              <Field label="Teljesítés">{dateShort(data.performanceDate)}</Field>
              <Field label="Esedékesség">{dateShort(data.paymentDueDate)}</Field>
              <Field label="ÁFA kategória">
                {data.vatCategory ? hu.vatCategory[data.vatCategory] : '—'}
              </Field>
              <Field label="Nettó"><span className="num">{money(data.amounts.net, data.amounts.currency)}</span></Field>
              <Field label="ÁFA">
                <span className="num">{money(data.amounts.vat, data.amounts.currency)}</span>
                {data.vatRatePercent ? ` (${data.vatRatePercent}%)` : ''}
              </Field>
              <Field label="Bruttó">
                <span className="num font-semibold">{money(data.amounts.gross, data.amounts.currency)}</span>
                {data.amounts.currency !== 'HUF' && data.grossAmountHuf != null && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    ≈ {money(data.grossAmountHuf, 'HUF')} (MNB)
                  </div>
                )}
              </Field>
              <Field label="Deviza / árfolyam">
                {data.amounts.currency}
                {data.amounts.exchangeRate ? ` · ${data.amounts.exchangeRate} HUF` : ''}
              </Field>
              {data.category && <Field label="Kategória">{data.category}</Field>}
              {data.projectCode && <Field label="Projekt">{data.projectCode}</Field>}
              {data.uploadedBy?.name && (
                <Field label="Prezentálta">
                  <div className="text-sm">
                    {data.uploadedBy.name}
                    {data.uploadedBy.email && (
                      <div className="text-xs text-slate-500 dark:text-slate-400">{data.uploadedBy.email}</div>
                    )}
                    {data.createdAt && (
                      <div className="text-xs text-slate-500 dark:text-slate-400">{dateTime(data.createdAt)}</div>
                    )}
                  </div>
                </Field>
              )}
              {data.paidBy && (
                <Field label="Ki fizette">
                  {data.paidBy === 'personal' ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200">
                      Saját kártya / zsebből
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-200">
                      Céges
                    </span>
                  )}
                </Field>
              )}
            </dl>
            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              <AiSuggestionsPanel invoiceId={data.id} />
            </div>
          </Card>

          <Card>
            <Tabs tabs={tabs} />
          </Card>
        </div>
      </div>

      <Modal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        title="Számla archiválása"
        footer={
          <>
            <Button variant="secondary" onClick={() => setArchiveOpen(false)}>Mégse</Button>
            <Button
              variant="danger"
              loading={archive.isPending}
              disabled={archiveReason.trim().length < 3}
              onClick={async () => {
                await archive.mutateAsync({ id: data.id, reason: archiveReason })
                toast.show({ tone: 'success', title: 'Archiválva' })
                setArchiveOpen(false)
                nav('/invoices')
              }}
            >
              Archiválás
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Az archiválás soft delete — a számla nem törlődik, de eltűnik az aktív listából.
            A művelet auditálva lesz.
          </p>
          <div>
            <Label htmlFor="archive-reason">Indoklás (kötelező)</Label>
            <Textarea
              id="archive-reason"
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              rows={3}
              placeholder="pl. Téves küldés, ugyanaz mint SZ2026/124"
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}

function downloadPdf(bytes: Uint8Array | undefined, basename: string) {
  if (!bytes) return
  // A TS 5.7 Uint8Array<ArrayBufferLike> nem assignable BlobPart-nak közvetlenül;
  // egy friss ArrayBuffer view-val biztonságos.
  const blob = new Blob([bytes.slice().buffer], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${basename.replace(/[^a-zA-Z0-9_\-./]/g, '_')}.pdf`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">{children}</dd>
    </div>
  )
}

function MatchingPanel({ inv }: { inv: InvoiceDetail }) {
  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center gap-3">
        <MatchingStatusBadge status={inv.matching.status} />
        <MatchingScoreBadge score={inv.matching.score} />
        <span className="text-xs text-slate-500">algoritmus: {inv.matching.algorithmVersion}</span>
      </div>

      <div>
        <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Mező-score-ok</h4>
        <ul className="space-y-1">
          {inv.matching.fieldScores.map((f) => (
            <li key={f.field} className="flex items-center justify-between">
              <span className="text-slate-700">{f.field}</span>
              <span className="num font-medium">{percent(f.score)}</span>
            </li>
          ))}
        </ul>
      </div>

      {inv.matching.discrepancies.length > 0 && (
        <div>
          <h4 className="text-xs uppercase tracking-wide text-amber-700 mb-2">Eltérések</h4>
          <ul className="space-y-1">
            {inv.matching.discrepancies.map((d, i) => (
              <li key={i} className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                <strong>{d.field}</strong>: NAV {String(d.navValue)} ≠ doc {String(d.docValue)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {inv.files.navXml && (
        <div>
          <a
            href={inv.files.navXml.downloadUrl}
            className="inline-flex items-center gap-1 text-sm text-brand-600 hover:underline"
          >
            <Download className="h-3 w-3" /> NAV XML letöltése
          </a>
        </div>
      )}
    </div>
  )
}

function SourcePanel({ inv }: { inv: InvoiceDetail }) {
  return (
    <div className="space-y-2 text-sm">
      <p>
        Forrás: <span className="font-medium">{hu.source[inv.source]}</span>
      </p>
      {inv.source === 'email' && inv.files.originalEmail && (
        <p>
          Eredeti email:{' '}
          <a href={inv.files.originalEmail.downloadEmlUrl} className="text-brand-600 hover:underline">
            .eml letöltés
          </a>
        </p>
      )}
      {inv.source === 'manual' && <p className="text-slate-600">Kézi feltöltés</p>}
      {inv.source === 'nav' && <p className="text-slate-600">NAV szinkronból érkezett</p>}
      <p className="text-slate-500 text-xs">
        Extraction confidence: {inv.extractionConfidence ?? '—'}%
      </p>
    </div>
  )
}

function AuditPanel({ inv }: { inv: InvoiceDetail }) {
  return (
    <ol className="space-y-2 text-sm">
      {inv.workflow.history.map((h, i) => (
        <li key={i} className="flex items-start gap-3 border-l-2 border-slate-200 pl-3">
          <div className="flex-1">
            <div className="text-slate-900">
              {h.fromStatus ? hu.invoiceStatus[h.fromStatus] : '—'} →{' '}
              <strong>{hu.invoiceStatus[h.toStatus]}</strong>
            </div>
            {h.reason && <div className="text-xs text-slate-600">{h.reason}</div>}
            <div className="text-xs text-slate-500">
              {dateTime(h.at)} · {h.actor}
            </div>
          </div>
        </li>
      ))}
    </ol>
  )
}

function CommentsPanel({ invoiceId }: { invoiceId: string }) {
  const { session, activeRole } = useAuth()
  const { data, isLoading } = useInvoiceComments(invoiceId)
  const add = useAddInvoiceComment()
  const toast = useToast()
  const [text, setText] = useState('')

  async function submit() {
    if (!session) return
    const body = text.trim()
    if (!body) {
      toast.show({ tone: 'warning', title: 'Üres megjegyzés nem rögzíthető' })
      return
    }
    try {
      await add.mutateAsync({ id: invoiceId, body, author: session.user, authorRole: activeRole })
      setText('')
      toast.show({ tone: 'success', title: 'Megjegyzés rögzítve' })
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'Hiba',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    }
  }

  const comments = data ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <MessageSquare className="h-3.5 w-3.5" />
        {isLoading ? 'Megjegyzések betöltése…' : `${comments.length} megjegyzés`}
      </div>

      {comments.length === 0 && !isLoading && (
        <p className="rounded border border-dashed border-slate-200 px-3 py-4 text-center text-sm text-slate-500">
          Még nincs megjegyzés ehhez a számlához. Az alábbi mezőbe írhatsz egyet.
        </p>
      )}

      <ul className="space-y-2">
        {comments.map((c) => (
          <li key={c.id} className="rounded border border-slate-200 bg-white px-3 py-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-slate-900">{c.authorName}</span>
              <span className="text-xs text-slate-500">{dateTime(c.createdAt)}</span>
            </div>
            {c.authorRole && (
              <div className="mt-0.5 text-xs text-slate-500">{hu.roles[c.authorRole]}</div>
            )}
            <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-800">{c.body}</p>
          </li>
        ))}
      </ul>

      <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-3">
        <Label htmlFor="new-comment">Új megjegyzés</Label>
        <Textarea
          id="new-comment"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="Pl. Beszállítóval egyeztetve, holnap zárjuk."
          maxLength={2000}
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-xs text-slate-500">
            Belső megjegyzés — auditolt, könyvelőnek átadáskor is látható lesz.
          </span>
          <Button
            type="button"
            size="sm"
            onClick={submit}
            loading={add.isPending}
            leftIcon={<Send className="h-3.5 w-3.5" />}
          >
            Hozzáfűzés
          </Button>
        </div>
      </div>
    </div>
  )
}

function PaymentPanel({ inv }: { inv: InvoiceDetail }) {
  const { activeRole } = useAuth()
  const canRecord = hasPermission(activeRole, 'invoices.changeStatus')
  const [open, setOpen] = useState(false)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm">
          <span className="text-slate-600 dark:text-slate-400">Jelenlegi fizetési státusz:</span>{' '}
          <PaymentStatusBadge status={inv.payment.status} />
        </div>
        {canRecord && (
          <Button size="sm" onClick={() => setOpen(true)} leftIcon={<CheckCircle2 className="h-4 w-4" />}>
            Fizetés rögzítése
          </Button>
        )}
      </div>

      {inv.payment.records.length === 0 ? (
        <p className="text-xs text-slate-500 dark:text-slate-400 rounded border border-dashed border-slate-300 dark:border-slate-700 px-3 py-3">
          Nincs külön rögzített fizetési tranzakció. A státuszváltozás auditolva van az Audit log alatt.
        </p>
      ) : (
        <ul className="space-y-2 text-sm">
          {inv.payment.records.map((r) => (
            <li key={r.id} className="flex items-center justify-between rounded border border-slate-200 dark:border-slate-800 px-3 py-2">
              <div>
                <div className="font-medium">
                  {moneyCompact(r.paidAmount, r.currency)} · {r.method}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {dateShort(r.paidAt)} · {r.reference ?? '—'}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <RecordPaymentModal
        open={open}
        onClose={() => setOpen(false)}
        invoiceId={inv.id}
        defaultAmount={inv.amounts.gross ?? 0}
        currency={inv.amounts.currency}
      />
    </div>
  )
}

function RecordPaymentModal({
  open,
  onClose,
  invoiceId,
  defaultAmount,
  currency,
}: {
  open: boolean
  onClose: () => void
  invoiceId: string
  defaultAmount: number
  currency: string
}) {
  const toast = useToast()
  const record = useRecordPayment()

  const [status, setStatus] = useState<'paid' | 'partially_paid' | 'unpaid' | 'overdue'>('paid')
  const [amount, setAmount] = useState<number>(defaultAmount)
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10))
  const [method, setMethod] = useState<'bank_transfer' | 'cash' | 'card' | 'other'>('bank_transfer')
  const [reference, setReference] = useState('')

  useEffect(() => {
    if (open) {
      setAmount(defaultAmount)
      setPaidAt(new Date().toISOString().slice(0, 10))
      setStatus('paid')
      setMethod('bank_transfer')
      setReference('')
    }
  }, [open, defaultAmount])

  const needsAmount = status === 'paid' || status === 'partially_paid'

  async function submit() {
    try {
      await record.mutateAsync({
        invoiceId,
        payload: {
          status,
          ...(needsAmount ? { paidAmount: amount, paidAt, method, reference: reference || undefined } : {}),
        },
      })
      toast.show({
        tone: 'success',
        title: 'Fizetés rögzítve',
        description:
          status === 'paid'
            ? `Teljes kifizetés: ${moneyCompact(amount, currency)}`
            : status === 'partially_paid'
              ? `Részfizetés: ${moneyCompact(amount, currency)}`
              : `Új státusz: ${hu.paymentStatus[status]}`,
      })
      onClose()
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'Hiba',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Fizetés rögzítése"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Mégse
          </Button>
          <Button
            onClick={submit}
            loading={record.isPending}
            disabled={needsAmount && (!amount || !paidAt)}
            leftIcon={<CheckCircle2 className="h-4 w-4" />}
          >
            Rögzít
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="pay-status">Új fizetési státusz</Label>
          <select
            id="pay-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
            className="block w-full rounded-md border border-slate-300 dark:border-slate-700 h-10 px-3 text-sm bg-white dark:bg-slate-900 dark:text-slate-100"
          >
            <option value="paid">Fizetve (teljes)</option>
            <option value="partially_paid">Részben fizetve</option>
            <option value="unpaid">Fizetetlen (vissza)</option>
            <option value="overdue">Lejárt</option>
          </select>
        </div>

        {needsAmount && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pay-amount">Összeg ({currency})</Label>
                <Input
                  id="pay-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                />
              </div>
              <div>
                <Label htmlFor="pay-date">Fizetés dátuma</Label>
                <Input
                  id="pay-date"
                  type="date"
                  value={paidAt}
                  onChange={(e) => setPaidAt(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pay-method">Mód</Label>
                <select
                  id="pay-method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value as typeof method)}
                  className="block w-full rounded-md border border-slate-300 dark:border-slate-700 h-10 px-3 text-sm bg-white dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="bank_transfer">Banki átutalás</option>
                  <option value="card">Bankkártya</option>
                  <option value="cash">Készpénz</option>
                  <option value="other">Egyéb</option>
                </select>
              </div>
              <div>
                <Label htmlFor="pay-ref">Referencia / közlemény</Label>
                <Input
                  id="pay-ref"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="opcionális"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

/**
 * CostAllocationPanel — fizetésmód + felelős + projekt + személyenkénti felosztás.
 *
 * Az invoice ki van fizetve cégesen (paidBy='company') vagy magánból (paidBy='personal').
 * Ha magán, akkor `paidByUserId` jelöli, ki tette ki a pénzt — ezt a cég vissza
 * fogja téríteni (az ő saját split-részét levonva).
 *
 * A splits 0–N sor: (user, %, opc Ft, opc note). Az összeg < 100% lehet (a
 * maradék implicit a cég-rész). > 100% szerver-oldali validációs hiba.
 */
function CostAllocationPanel({ inv }: { inv: InvoiceDetail }) {
  const toast = useToast()
  const usersQ = useUsers()
  const mutation = useUpdateCostAllocation()

  const [paidBy, setPaidBy] = useState<'company' | 'personal' | ''>(
    inv.paidBy === 'company' || inv.paidBy === 'personal' ? inv.paidBy : '',
  )
  const [paidByUserId, setPaidByUserId] = useState<string>(inv.paidByUserId ?? '')
  const [projectCode, setProjectCode] = useState<string>(inv.projectCode ?? '')
  const [category, setCategory] = useState<string>(inv.category ?? '')
  type EditSplit = { userId: string; percent: string; amount: string; note: string }
  const [splits, setSplits] = useState<EditSplit[]>(
    (inv.splits ?? []).map((s: InvoiceSplit) => ({
      userId: s.userId,
      percent: String(s.percent),
      amount: s.amount != null ? String(s.amount) : '',
      note: s.note ?? '',
    })),
  )

  // Friss invoice payload érkezhet — szinkronizáljuk a state-et a "kanonikus"
  // szerver oldali értékhez (csak ha az invoice ID változik). Ez kerüli el a
  // ciklikus update-eket szerkesztés közben.
  useEffect(() => {
    setPaidBy(inv.paidBy === 'company' || inv.paidBy === 'personal' ? inv.paidBy : '')
    setPaidByUserId(inv.paidByUserId ?? '')
    setProjectCode(inv.projectCode ?? '')
    setCategory(inv.category ?? '')
    setSplits(
      (inv.splits ?? []).map((s) => ({
        userId: s.userId,
        percent: String(s.percent),
        amount: s.amount != null ? String(s.amount) : '',
        note: s.note ?? '',
      })),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv.id])

  const users = usersQ.data ?? []
  const gross = inv.amounts.gross ?? 0
  const sumPercent = splits.reduce((a, s) => a + (Number(s.percent) || 0), 0)
  const remainderPercent = Math.max(0, 100 - sumPercent)
  const remainderAmount = (gross * remainderPercent) / 100
  const isOver = sumPercent > 100.01

  function addSplit() {
    setSplits((s) => [...s, { userId: users[0]?.id ?? '', percent: '0', amount: '', note: '' }])
  }
  function removeSplit(idx: number) {
    setSplits((s) => s.filter((_, i) => i !== idx))
  }
  function updateSplit(idx: number, patch: Partial<EditSplit>) {
    setSplits((s) => s.map((row, i) => (i === idx ? { ...row, ...patch } : row)))
  }

  async function onSave() {
    if (isOver) {
      toast.show({ tone: 'error', title: 'A felosztás meghaladja a 100%-ot', description: `Jelenleg ${sumPercent.toFixed(2)}%` })
      return
    }
    if (paidBy === 'personal' && !paidByUserId) {
      toast.show({ tone: 'error', title: 'Adj meg kifizetőt', description: 'Magán-kifizetésnél kötelező megadni, ki fizette ki.' })
      return
    }
    const payload: CostAllocationPayload = {
      paidBy: paidBy || null,
      paidByUserId: paidBy === 'personal' ? paidByUserId || null : null,
      projectCode: projectCode.trim() || null,
      category: category.trim() || null,
      splits: splits
        .filter((s) => s.userId && Number(s.percent) > 0)
        .map((s) => ({
          userId: s.userId,
          percent: Number(s.percent),
          amount: s.amount ? Number(s.amount) : null,
          note: s.note.trim() || null,
        })),
    }
    try {
      await mutation.mutateAsync({ invoiceId: inv.id, payload })
      toast.show({ tone: 'success', title: 'Költségfelosztás mentve' })
    } catch (err) {
      toast.show({ tone: 'error', title: 'Mentés nem sikerült', description: err instanceof Error ? err.message : String(err) })
    }
  }

  return (
    <div className="space-y-4 p-4 text-sm">
      <div className="rounded-md border border-slate-200 bg-slate-50 dark:bg-slate-800 dark:border-slate-700 p-3">
        <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">Számla bruttó értéke</div>
        <div className="font-mono text-lg font-semibold">{money(gross, inv.amounts.currency)}</div>
      </div>

      {/* Fizetésmód + kifizető */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="cost-paidby">Fizetésmód</Label>
          <select
            id="cost-paidby"
            value={paidBy}
            onChange={(e) => setPaidBy(e.target.value as 'company' | 'personal' | '')}
            className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 text-sm"
          >
            <option value="">— nincs megadva —</option>
            <option value="company">Céges (átutalás / céges kártya)</option>
            <option value="personal">Magán (saját kártya / zsebből)</option>
          </select>
        </div>
        {paidBy === 'personal' && (
          <div>
            <Label htmlFor="cost-paidbyuser">Ki fizette ki</Label>
            <select
              id="cost-paidbyuser"
              value={paidByUserId}
              onChange={(e) => setPaidByUserId(e.target.value)}
              className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 text-sm"
            >
              <option value="">— válassz —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Projekt + kategória */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="cost-project">Projekt kód</Label>
          <Input
            id="cost-project"
            value={projectCode}
            onChange={(e) => setProjectCode(e.target.value)}
            placeholder="pl. PROJ-2026-A vagy SZF-marketing"
          />
        </div>
        <div>
          <Label htmlFor="cost-category">Kategória</Label>
          <Input
            id="cost-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="pl. szoftver, utazás, irodaszer"
          />
        </div>
      </div>

      {/* Splits */}
      <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-medium">Személyenkénti felosztás</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Pl. „A10 vagy R10” = András / Robi 10%-a. A maradék implicit a cégé.
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={addSplit} disabled={users.length === 0}>
            + Új sor
          </Button>
        </div>
        {splits.length === 0 && (
          <div className="rounded-md border border-dashed border-slate-300 dark:border-slate-700 px-3 py-4 text-center text-xs text-slate-500">
            Nincs felosztás — a teljes összeg a cég-rész.
          </div>
        )}
        {splits.length > 0 && (
          <div className="space-y-2">
            {splits.map((row, idx) => {
              const pct = Number(row.percent) || 0
              const calcAmt = (gross * pct) / 100
              return (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 items-end rounded-md border border-slate-200 dark:border-slate-700 p-2"
                >
                  <div className="col-span-12 sm:col-span-4">
                    <Label className="text-xs">Személy</Label>
                    <select
                      value={row.userId}
                      onChange={(e) => updateSplit(idx, { userId: e.target.value })}
                      className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 text-sm"
                    >
                      <option value="">—</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.fullName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <Label className="text-xs">%</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={row.percent}
                      onChange={(e) => updateSplit(idx, { percent: e.target.value })}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <Label className="text-xs">Ft (opc)</Label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      value={row.amount}
                      onChange={(e) => updateSplit(idx, { amount: e.target.value })}
                      placeholder={Math.round(calcAmt).toLocaleString('hu-HU')}
                    />
                  </div>
                  <div className="col-span-10 sm:col-span-3">
                    <Label className="text-xs">Megjegyzés</Label>
                    <Input
                      value={row.note}
                      onChange={(e) => updateSplit(idx, { note: e.target.value })}
                      placeholder="opc."
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => removeSplit(idx)} aria-label="Sor törlése">
                      ×
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        <div className="mt-3 rounded-md border border-slate-200 dark:border-slate-700 p-3 text-xs">
          <div className="flex items-center justify-between">
            <span>Összesen kiosztva:</span>
            <span className={isOver ? 'font-semibold text-red-600' : 'font-semibold'}>
              {sumPercent.toFixed(2)}% · {money((gross * sumPercent) / 100, inv.amounts.currency)}
            </span>
          </div>
          <div className="flex items-center justify-between mt-1 text-slate-500 dark:text-slate-400">
            <span>Cég-rész (maradék):</span>
            <span>
              {remainderPercent.toFixed(2)}% · {money(remainderAmount, inv.amounts.currency)}
            </span>
          </div>
          {isOver && (
            <div className="mt-2 text-red-600">A felosztás meghaladja a 100%-ot — javítsd a sorokat.</div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button onClick={onSave} loading={mutation.isPending} disabled={isOver}>
          Mentés
        </Button>
      </div>
    </div>
  )
}
